import useI18n from '@/shared/hooks/usei18n'
import { Button, GenericFullscreenDialog } from '@/common'
import {
  ImporterFormData,
  validateImporterForm,
} from '../../../components/ImporterForm.schema'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Importer,
  useUpdateImporter,
  ValidationErrorResponse,
} from '@/features/importers/hooks/importers'
import toast from 'react-hot-toast'
import ImporterForm from '../../../components/ImporterForm'

export default function ImporterEditDialog({
  isOpen,
  close,
  importer,
  organizationId,
}: {
  isOpen: boolean
  close: () => void
  importer: Importer
  organizationId: string
}) {
  const { t } = useI18n()

  const { mutateAsync: updateImporter, isPending: isSaving } =
    useUpdateImporter(organizationId, importer.id)

  const [formData, setFormData] = useState<ImporterFormData>({
    name: '',
    customId: '',
    placeOfRegistration: '',
    identificationNumber: '',
    street: '',
    streetNumber: '',
    street2: '',
    postCode: '',
    city: '',
    subDivision: '',
    country: '',
    poBox: '',
  })

  const [fieldErrors, setFieldErrors] = useState<{
    customId?: string
    identificationNumber?: string
  }>({})

  const resetFormData = useCallback(() => {
    if (importer) {
      setFormData({
        name: importer.name,
        customId: importer.customId || '',
        placeOfRegistration: importer.placeOfRegistration,
        identificationNumber: importer.identificationNumber,
        street: importer.street || '',
        streetNumber: importer.streetNumber || '',
        street2: importer.street2 || '',
        postCode: importer.postCode || '',
        city: importer.city || '',
        poBox: importer.poBox || '',
        subDivision: importer.subDivision || '',
        country: importer.country,
      })
      setFieldErrors({})
    }
  }, [importer])

  useEffect(() => {
    resetFormData()
  }, [resetFormData])

  const isSaveDisabled = useMemo(() => {
    const validation = validateImporterForm(formData)
    return isSaving || !validation.success
  }, [isSaving, formData])

  const onClose = useCallback(() => {
    resetFormData()
    setFieldErrors({})
    close()
  }, [close, resetFormData])

  const onSave = useCallback(async () => {
    try {
      await updateImporter({
        ...formData,
        customId: formData.customId || undefined,
      })
      toast.success(
        t(
          'importers:editImporterDialog.notifications.importerUpdatedSuccessfully'
        )
      )
      onClose()
    } catch (error: any) {
      console.error(error)
      const errorData = error?.response?.data as
        | ValidationErrorResponse
        | undefined

      if (errorData?.errors && Array.isArray(errorData.errors)) {
        errorData.errors.forEach((err) => {
          switch (err.message) {
            case 'CUSTOM_ID_ALREADY_EXISTS':
              setFieldErrors((prev) => ({
                ...prev,
                customId: t('importers:errors.customIdAlreadyExists'),
              }))
              break
            case 'IDENTIFICATION_NUMBER_ALREADY_EXISTS':
              setFieldErrors((prev) => ({
                ...prev,
                identificationNumber: t(
                  'importers:errors.identificationNumberAlreadyExists'
                ),
              }))
              break
          }
        })
      } else {
        toast.error(
          t('importers:editImporterDialog.notifications.failedToUpdateImporter')
        )
      }
    }
  }, [updateImporter, formData, onClose, t])

  return (
    <GenericFullscreenDialog
      isOpen={isOpen}
      close={close}
      title={t('importers:editImporterDialog.title')}
      actions={
        <div className="flex justify-end gap-2">
          <Button variant="neutral" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button disabled={isSaving || isSaveDisabled} onClick={onSave}>
            {t('importers:editImporterDialog.actions.save')}
          </Button>
        </div>
      }
    >
      <ImporterForm
        data={formData}
        onChange={setFormData}
        fieldErrors={fieldErrors}
        resetErrorField={(field) => {
          setFieldErrors((prev) => ({ ...prev, [field]: undefined }))
        }}
      />
    </GenericFullscreenDialog>
  )
}
